"use client";

import { JSX, ReactNode, useEffect, useMemo, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { useLocale } from "next-intl";
import { cn } from "@/lib/utils";
import {
  Footer,
  FooterBottom,
  FooterColumn,
  FooterContent,
} from "@/components/ui/footer";
import { ModeToggle } from "@/components/ui/mode-toggle";
import { LangToggle } from "@/components/ui/lang-toggle";
import ParallaxBanner from "./ui/parallax-banner";
import {
  IconMailFilled,
  IconMapPinFilled,
  IconPhoneFilled,
} from "@tabler/icons-react";
import SocialIconButton from "./social-media";
import { DEFAULT_HOMEPAGE_SECTIONS, type LocaleCode } from "@/types/sections";
import { getLegalHref, getLegalNav } from "@/data/legals";
import { getLocalizedHref } from "@/lib/site";

// 🔹 Firestore
import { db } from "@/lib/firebase.client"; // your client init
import { doc, onSnapshot } from "firebase/firestore";

interface FooterLink {
  text: string;
  icon?: JSX.Element | null;
  href: string;
}

interface FooterColumnProps {
  title: string;
  links: FooterLink[];
  social?: boolean;
  map?: boolean;
}

interface FooterProps {
  logo?: ReactNode;
  name?: string;
  columns?: FooterColumnProps[]; // (kept for fallback)
  copyright?: string;
  policies?: FooterLink[];
  showModeToggle?: boolean;
  className?: string;
}

type FSMenuItem = { label: string; href: string; children?: FSMenuItem[] };
type SocialPlatform = "facebook" | "x" | "instagram" | "youtube" | "linkedin";
type FSSettings = {
  maintenance: boolean;
  confidentialCode?: string;
  menus?: {
    footer?: {
      en?: FSMenuItem[];
      fr?: FSMenuItem[];
    };
  };
  contact?: {
    address?: string;
    phone?: string;
    phoneSecondary?: string;
    email?: string;
    hours?: { en?: string; fr?: string };
    googleMapIframe?: string;
  };
  title: { en?: string; fr?: string };
  description: { en?: string; fr?: string };
  socials?: { platform: SocialPlatform; url: string }[];
};
const EXTERNAL_OR_SPECIAL_HREF = /^[a-z][a-z\d+.-]*:/i;

function isExternalOrSpecialHref(href: string) {
  return EXTERNAL_OR_SPECIAL_HREF.test(href) || href.startsWith("//");
}

// Optional: mirror one locale to the other if missing
function mirrorLocale<T>(
  val: { en?: T; fr?: T },
  fallbackLocale: "en" | "fr" = "en"
): { en?: T; fr?: T } {
  const en = val?.en;
  const fr = val?.fr;

  // both present → nothing to do
  if (en && fr) return val ?? {};

  // only one present → mirror it to the other, honoring fallbackLocale
  const v = en ?? fr;
  if (!v) return val ?? {};
  return fallbackLocale === "en"
    ? { en: v, fr: fr ?? v }
    : { en: en ?? v, fr: v };
}

export default function FooterSection({
  logo = (
    <Image
      src="/logo.svg"
      alt="Uncommon12"
      id="footer-logo"
      width={200}
      height={200}
      className="mx-auto rounded-xl p-2"
      loading="eager"
    />
  ),
  // Fallbacks (used if Firestore hasn’t loaded yet)
  columns: fallbackColumns = [],
  copyright,
  policies = [],
  showModeToggle = true,
  className,
}: FooterProps) {
  const locale = (useLocale() as LocaleCode) ?? "en";

  const [settings, setSettings] = useState<FSSettings | null>(null);

  // Subscribe to the singleton settings doc
  useEffect(() => {
    const docId = process.env.NEXT_PUBLIC_WEBSITE_SETTINGS_DOC_ID || "default";
    const ref = doc(db, "website-settings", docId);

    const unsub = onSnapshot(ref, (snap) => {
      const data = snap.data() as FSSettings | undefined;

      // shallow normalize locales that might be missing
      const mirroredFooter = mirrorLocale(data?.menus?.footer ?? {});
      const mirroredHours = mirrorLocale(data?.contact?.hours ?? {});
      const mirroredTitle = mirrorLocale(data?.title ?? {});
      const mirroredDescription = mirrorLocale(data?.description ?? {});
      setSettings({
        ...data,
        maintenance: data?.maintenance ?? false,
        title: mirroredTitle,
        description: mirroredDescription,
        menus: { footer: mirroredFooter },
        contact: { ...data?.contact, hours: mirroredHours },
      });
    });
    return () => unsub();
  }, []);

  // Build the dynamic footer columns
  const dynamicColumns: FooterColumnProps[] = useMemo(() => {
    const sections = (settings?.menus?.footer?.[locale] ?? []) as FSMenuItem[];

    // Map sections -> columns (section label becomes column title, children become links)
    const sectionColumns: FooterColumnProps[] = sections.map((section) => ({
      title: section.label,
      links: (section.children ?? [])
        .filter((c) => c?.label) // skip malformed
        .map((c) => ({
          text: c.label,
          href: getLocalizedHref(locale, c.href || "#"),
        })),
    }));

    // Contact column (always last)
    const contactCol: FooterColumnProps = {
      title: locale === "fr" ? "Contact" : "Contact",
      links: [
        settings?.contact?.phone
          ? {
              icon: <IconPhoneFilled />,
              text: settings.contact.phone,
              href: `tel:${settings.contact.phone.replace(/\s+/g, "")}`,
            }
          : null,
        settings?.contact?.phoneSecondary
          ? {
              icon: <IconPhoneFilled />,
              text: settings.contact.phoneSecondary,
              href: `tel:${settings.contact.phoneSecondary.replace(/\s+/g, "")}`,
            }
          : null,
        settings?.contact?.email
          ? {
              icon: <IconMailFilled />,
              text: settings.contact.email,
              href: `mailto:${settings.contact.email}`,
            }
          : null,
        settings?.contact?.address
          ? {
              icon: <IconMapPinFilled />,
              text: `${settings.contact.address}`,
              href: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                settings.contact.address
              )}`,
            }
          : null,
      ].filter(Boolean) as FooterLink[],
      social: true,
      map: Boolean(settings?.contact?.googleMapIframe),
    };

    return [...sectionColumns, contactCol];
  }, [settings, locale]);

  // Socials from settings (falls back to placeholders only if missing)
  const socials = settings?.socials ?? [];
  const banner = DEFAULT_HOMEPAGE_SECTIONS.banners[0];
  const fallbackPolicies: FooterLink[] = getLegalNav(locale).map((item) => ({
    text: item.label,
    href: getLegalHref(locale, item.slug),
  }));
  const policyLinks = (policies && policies.length > 0 ? policies : fallbackPolicies) as FooterLink[];
  const fallbackCopyright =
    locale === "fr"
      ? "© 2025 Uncommon12. Tous droits réservés"
      : "© 2025 Uncommon12. All rights reserved";
  const copyrightText = copyright || fallbackCopyright;

  return (
    !(settings?.maintenance ?? true) && (
      <footer className={cn("bg-background w-full", className)}>
        <ParallaxBanner banner={banner} locale={locale} />

        <div className="max-w-container mx-auto px-4">
          <Footer>
            <FooterContent className="mx-auto">
              {/* Intro / Logo */}
              <FooterColumn className="col-span-1 sm:col-span-2 md:col-span-3 lg:col-span-1">
                <div className="flex px-5 pb-5 items-center gap-5 flex-col">
                  {logo}
                  <p className="max-w-[600px] lg:max-w-[15rem] text-neutral-600 dark:text-neutral-400 text-center">
                    {/* Optional: make this dynamic later (e.g., description.en/fr in settings) */}
                    {locale === "fr"
                      ? settings?.description?.fr
                      : settings?.description?.en}
                  </p>
                </div>
              </FooterColumn>

              {/* Dynamic columns from Firestore; fallback to provided columns if not loaded yet */}
              {(settings ? dynamicColumns : fallbackColumns).map(
                (column, index) => (
                  <FooterColumn key={index} className="mx-auto w-full p-5">
                    <h3 className="text-md pt-1 font-semibold">
                      {column.title}
                    </h3>

                    {column.links.map((link, linkIndex) => {
                      const href = getLocalizedHref(locale, link.href || "#");

                      return isExternalOrSpecialHref(href) ? (
                        <a
                          key={linkIndex}
                          href={href}
                          target={href.startsWith("http") ? "_blank" : undefined}
                          rel={href.startsWith("http") ? "noreferrer" : undefined}
                          className="text-muted-foreground text-sm flex items-center gap-2"
                        >
                          {link.icon ?? <></>}
                          {link.text}
                        </a>
                      ) : (
                        <Link
                          key={linkIndex}
                          href={href}
                          className="text-muted-foreground text-sm flex items-center gap-2"
                        >
                          {link.icon ?? <></>}
                          {link.text}
                        </Link>
                      );
                    })}

                    {/* Google Map iframe from settings, if provided */}
                    {column.map && settings?.contact?.googleMapIframe ? (
                      <iframe
                        title="Uncommon12 location"
                        src={settings?.contact?.googleMapIframe}
                        className="rounded-lg"
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                      ></iframe>
                    ) : null}

                    {/* Socials from settings */}
                    {column.social ? (
                      <div className="flex gap-4 items-center justify-center mt-2">
                        {socials.length > 0 ? (
                          socials.map((s, i) => (
                            <SocialIconButton
                              key={i}
                              platform={s.platform}
                              href={s.url}
                            />
                          ))
                        ) : (
                          <>
                            {/* Optional: remove this fallback block if you don't want placeholders */}
                            <SocialIconButton
                              platform="facebook"
                              href="https://facebook.com"
                            />
                            <SocialIconButton
                              platform="x"
                              href="https://twitter.com"
                            />
                            <SocialIconButton
                              platform="instagram"
                              href="https://instagram.com"
                            />
                            <SocialIconButton
                              platform="youtube"
                              href="https://youtube.com"
                            />
                          </>
                        )}
                      </div>
                    ) : null}
                  </FooterColumn>
                )
              )}
            </FooterContent>

            <FooterBottom>
              <div>{copyrightText}</div>
              <div className="flex items-center gap-4">
                {policyLinks.map((policy, index) => {
                  const href = getLocalizedHref(locale, policy.href || "#");

                  return isExternalOrSpecialHref(href) ? (
                    <a key={index} href={href}>
                      {policy.text}
                    </a>
                  ) : (
                    <Link key={index} href={href}>
                      {policy.text}
                    </Link>
                  );
                })}
                {showModeToggle && <LangToggle />}
                {showModeToggle && <ModeToggle />}
              </div>
            </FooterBottom>
          </Footer>
        </div>
      </footer>
    )
  );
}
